import { BillingType, IOperationResultNoValue, ISystemJob } from '@ViewModels';
import { css } from 'aphrodite';
import { makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { MastheadPortalId } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import {
	AccountFilterCriteriaProperty,
	AccountsAdminViewModel,
	AdminReportingViewModel,
} from '../../../../viewmodels/AdminViewModels';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../../web/components/Breadcrumb';
import { ExportConfirmationModal } from '../../../../web/components/ExportConfirmation';
import { Portal } from '../../../../web/components/Portal';
import { DownloadIcon } from '../../../../web/components/svgs/icons/DownloadIcon';
import { ReportRowAccountChange } from '../../../components/reporting/ReportRowAccountChange';
import { ReportRowPerson } from '../../../components/reporting/ReportRowPerson';
import { styleSheet } from './styles';

interface IProps extends IErrorMessageComponentProps, IUserSessionComponentProps {}
interface IState {
	reportsVM: AdminReportingViewModel;
}

class _ReportsDailyActiveUse extends React.Component<IProps, IState> {
	private mExportPromise: Promise<ISystemJob>;
	private mAccountsAdminViewModel: AccountsAdminViewModel;
	constructor(props: IProps) {
		super(props);

		makeObservable<_ReportsDailyActiveUse, 'mExportPromise'>(this, {
			mExportPromise: observable.ref,
		});

		this.mAccountsAdminViewModel = new AccountsAdminViewModel(props.userSession);
		this.state = {
			reportsVM: new AdminReportingViewModel(props.userSession),
		};
	}

	public componentDidMount() {
		const { reportsVM } = this.state;
		const { errorMessages } = this.props;
		reportsVM
			.loadAll()
			?.then(() => reportsVM.loadAccountsChanged())
			?.catch((err: IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
			});
	}

	public render() {
		const { reportsVM } = this.state;

		return (
			<div className={css(styleSheet.container)}>
				<Portal destination={MastheadPortalId}>
					<Breadcrumb>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to='/reports'>Reports</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbPage>Daily Renewals</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
				</Portal>
				<div className={css(styleSheet.titleRow)}>
					<div className={css(styleSheet.title)}>Daily Active Use Report</div>
					<button className={css(styleSheet.downloadButton)} onClick={this.exportActiveUse}>
						<DownloadIcon />
						<span className={css(styleSheet.download)}>Download Full Report</span>
					</button>
				</div>
				<div className={css(styleSheet.dailyUseRow)}>
					<div className={css(styleSheet.dailyUseLeft)}>
						<div className={css(styleSheet.totalUse)}>
							{reportsVM.validDailyActiveUse}
							<div className={css(styleSheet.dailyActiveUseSubtitle)}>Daily Active Use</div>
						</div>
						<div className={css(styleSheet.totalIndustry)}>
							<div className={css(styleSheet.titleIndustry)}>Insurance</div>
							{reportsVM.validInsuranceActiveUse}
						</div>
						<div className={css(styleSheet.totalIndustry)}>
							<div className={css(styleSheet.titleIndustry)}>Real Estate</div>
							{reportsVM.validRealEstateActiveUse}
						</div>
						<div className={css(styleSheet.totalIndustry)}>
							<div className={css(styleSheet.titleIndustry)}>Financial</div>
							{reportsVM.validFinancialActiveUse}
						</div>
						<div className={css(styleSheet.totalIndustry)}>
							<div className={css(styleSheet.titleIndustry)}>Mortgage</div>
							{reportsVM.validMortgageActiveUse}
						</div>
						<div className={css(styleSheet.totalIndustry)}>
							<div className={css(styleSheet.titleIndustry)}>Legal</div>
							{reportsVM.validLegalActiveUse}
						</div>
					</div>
					<div className={css(styleSheet.dailyUseRight)}>
						<div className={css(styleSheet.percentDisplay)}>
							<div className={css(styleSheet.gray)}>Gray</div>
							{reportsVM.validGrayActiveUse}
						</div>
						<div className={css(styleSheet.percentDisplay)}>
							<div className={css(styleSheet.green)}>Green</div>
							{reportsVM.validGreenActiveUse}
						</div>
						<div className={css(styleSheet.percentDisplay)}>
							<div className={css(styleSheet.yellow)}>yellow</div>
							{reportsVM.validYellowActiveUse}
						</div>
						<div className={css(styleSheet.percentDisplay)}>
							<div className={css(styleSheet.orange)}>orange</div>
							{reportsVM.validOrangeActiveUse}
						</div>
						<div className={css(styleSheet.percentDisplay)}>
							<div className={css(styleSheet.newReds)}>new reds</div>
							{reportsVM.validNewRedActiveUse}
						</div>
					</div>
				</div>
				{reportsVM?.activeUseSummary?.byCustomerSuccess?.length > 0 && (
					<div>
						<div className={css(styleSheet.successTitleRow)}>
							<div className={css(styleSheet.successTitleCSM, styleSheet.gray)}>CSM</div>
							<div className={css(styleSheet.gray, styleSheet.header)}>ACTIVE USE</div>
							<div className={css(styleSheet.green, styleSheet.header)}>GREEN</div>
							<div className={css(styleSheet.yellow, styleSheet.header)}>YELLOW</div>
							<div className={css(styleSheet.orange, styleSheet.header)}>ORANGE</div>
							<div className={css(styleSheet.newReds, styleSheet.header)}>RED</div>
						</div>
						{reportsVM?.activeUseSummary?.byCustomerSuccess.map(csm => {
							return <ReportRowPerson activeUse={csm} key={csm.user.id} />;
						})}
					</div>
				)}
				{reportsVM?.accountsChanged?.length > 0 && !reportsVM.isLoadingAccountsChanged && (
					<div>
						<div className={css(styleSheet.accountChangeTitleRow)}>
							<div className={css(styleSheet.gray, styleSheet.accountChangeLargeTitle)}>
								ACCOUNTS WITH STATUS CHANGE
							</div>
							<div className={css(styleSheet.gray, styleSheet.accountChangeSmallTitle)}>CSM</div>
							<div className={css(styleSheet.gray, styleSheet.accountChangeSmallTitle)}>NEW STATUS</div>
						</div>
						{reportsVM?.accountsChanged.map(account => {
							return <ReportRowAccountChange account={account} key={account.accountId} />;
						})}
					</div>
				)}
				{!!this.mExportPromise && (
					<ExportConfirmationModal
						promise={this.mExportPromise}
						modalProps={{
							isOpen: !!this.mExportPromise,
							onRequestClose: this.onExportAccountsModalRequestClose,
						}}
					/>
				)}
			</div>
		);
	}

	private exportActiveUse = () => {
		const criteria = [
			{
				property: AccountFilterCriteriaProperty.AccountType,
				value: 'business',
			},
			{
				property: AccountFilterCriteriaProperty.BillingType,
				value: BillingType.Paid,
			},
		];

		this.mExportPromise = this.mAccountsAdminViewModel.exportAccounts(criteria, false);
	};

	private onExportAccountsModalRequestClose = () => {
		this.mExportPromise = null;
	};
}

const ReportsDailyActiveUseAsObserver = observer(_ReportsDailyActiveUse);
export const ReportsDailyActiveUse = inject(
	ErrorMessagesViewModelKey,
	UserSessionViewModelKey
)(ReportsDailyActiveUseAsObserver);
